import type { Dictionary } from '../i18n';

/**
 * French. Written as a working studio would say it, not translated word for
 * word. Where the English leans on darkroom vocabulary the French takes its
 * own — un tirage, un négatif, une planche contact, entourer et barrer — but
 * only where a photographer would still say it: a frame is une image, not une
 * vue, and a shoot is une séance, because "pellicule" standing alone in a menu
 * reads as dandruff long before it reads as film.
 *
 * Typed as a partial dictionary on purpose: a key that has not been translated
 * falls back to English, so adding a string never breaks this file.
 */
export const fr: Partial<Dictionary> = {
  /* ------------------------------- shell ------------------------------- */
  'app.skip': 'Aller au contenu',
  'app.sections': 'Sections du studio',
  'hub.eyebrow': 'Nouvelle séance',
  'hub.title': 'Qu’est-ce qu’on shoote aujourd’hui ?',
  'hub.lede': 'Trois façons de préparer un shooting. Chacune ne vous demande que ce dont elle a besoin.',
  'hub.recent': 'Séances récentes',
  'hub.live': 'En cours',
  'hub.copy.title': 'Recréer une scène',
  'hub.copy.text':
    'Choisissez une scène du Studio : votre mannequin et vos vêtements prennent la place des siens, tout le reste est identique.',
  'hub.copy.go': 'Choisir une scène',
  'hub.presets.title': 'Mannequin, lieux et poses',
  'hub.presets.text':
    'Un mannequin du Studio ou le vôtre, vos vêtements, des lieux et des poses en photo : chaque pose est reproduite à l’identique.',
  'hub.presets.go': 'Composer',
  'hub.brief.title': 'Décrire le shooting',
  'hub.brief.text':
    'Avec le styliste : il vous propose trois tenues, trois décors et trois poses, vous choisissez, puis vous ajustez version après version, comme sur un plateau.',
  'hub.brief.go': 'Parler au styliste',
  'stylist.title': 'Le styliste',
  'stylist.name': 'Le styliste',
  'stylist.fiche': 'Ce qui est décidé',
  'stylist.toChoose': 'à choisir',
  'stylist.step.model': 'Mannequin',
  'stylist.step.pieces': 'Articles',
  'stylist.say.model': 'Dites qui, ou touchez un portrait…',
  'stylist.say.pieces': 'Ou nommez les pièces…',
  'stylist.say.choose': '« La 2 », ou votre idée…',
  'stylist.say.review': 'Ce qu’il faut changer…',
  'stylist.typing': 'Le styliste écrit',
  'stylist.studioModel': 'Studio',
  'stylist.ownModel': 'Vos mannequins',
  'stylist.createModel': 'Créer un mannequin',
  'stylist.addPiece': 'Ajouter un article',
  'stylist.change': 'changer',
  'stylist.surprise': 'Surprends-moi',
  'stylist.develop': 'Pellicule & grain',
  'stylist.new': 'Nouvelle conversation',
  'stylist.openRoll': 'Voir la séance',
  'stylist.resume': 'Reprendre une conversation',
  'stylist.untitled': 'Sans titre',
  'stylist.loadFailed': 'La conversation n’a pas pu être ouverte.',
  'stylist.failed': 'Le styliste n’a pas pu répondre. Réessayez.',
  'stylist.ask.model': 'Bonjour ! Quel mannequin voulez-vous pour ce shooting ?',
  'stylist.ask.pieces': 'Très bien. Quels articles voulez-vous shooter ?',
  'stylist.piecesDone': 'C’est tout, propose-moi des tenues',
  'stylist.options.outfit': 'Voici trois façons de les porter. Laquelle vous plaît ?',
  'stylist.options.place': 'Pour cette tenue, trois décors :',
  'stylist.options.pose': 'Et trois poses pour ce décor :',
  'stylist.step.outfit': 'Tenue',
  'stylist.step.place': 'Décor',
  'stylist.step.pose': 'Pose',
  'stylist.other': 'Autre',
  'stylist.otherHint.outfit': 'Décrivez la tenue que vous voulez',
  'stylist.otherHint.place': 'Décrivez le décor que vous voulez',
  'stylist.otherHint.pose': 'Décrivez la pose que vous voulez',
  'stylist.otherGo': 'Choisir celle-ci',
  'stylist.more': 'D’autres idées',
  'stylist.makesVersion': 'Choisir lance une version · {cost}',
  'stylist.versionN': 'Version {n}',
  'stylist.correction': 'Correction',
  'stylist.shooting': 'Prise de vue en cours…',
  'stylist.correcting': 'Correction en cours…',
  'stylist.wait': 'Laissez-moi finir cette version d’abord ; dites-moi ensuite ce qu’il faut changer.',
  'stylist.unclear':
    'Je n’ai pas compris ce qu’il faut changer. Dites-moi la tenue, le décor ou la pose — par leur numéro ou avec vos mots — ou ce qu’il faut corriger sur la photo.',
  'stylist.finishes': 'Réglages de prise de vue',
  'stylist.costEach': '{cost} par version',
  'stylist.send': 'Envoyer',
  'stylist.idea.pose': 'J’aime pas la pose, mets la pose 2',
  'stylist.idea.place': 'Garde la tenue, mais avec le décor 3',
  'stylist.idea.blur': 'Plus de flou derrière elle',
  'stylist.idea.more': 'Propose-moi d’autres poses',
  'source.failed': 'Impossible d’importer cette image.',
  'source.notAnImage': 'Ce fichier n’est pas une image PNG, JPEG ou WebP.',
  'source.remove': 'Retirer l’image',
  'source.working': 'Import…',
  'source.dropOrClick': 'Glissez une image ici ou cliquez pour en choisir une',
  'source.urlPlaceholder': '… ou collez l’adresse d’une image ou d’une page',
  'source.urlLabel': 'Adresse d’une image ou d’une page web',
  'source.import': 'Importer',
  'scenewiz.step.model': 'Mannequin',
  'scenewiz.step.clothes': 'Vêtements',
  'scenewiz.step.scenes': 'Scènes',
  'scenewiz.step.outfit': 'Tenue',
  'scenewiz.step.finish': 'Finitions',
  'scenewiz.step.go': 'Lancer',
  'scenewiz.title.model': 'Qui porte vos vêtements ?',
  'scenewiz.title.clothes': 'Que porte-t-elle ?',
  'scenewiz.title.scenes': 'Dans quelles scènes ?',
  'scenewiz.title.outfit': 'Comment les porter ?',
  'scenewiz.title.finish': 'Quelques finitions',
  'scenewiz.title.go': 'Prêt à shooter',
  'scenewiz.lede.model': 'Un mannequin du Studio, ou le vôtre.',
  'scenewiz.lede.clothes': 'Ajoutez les photos de vos pièces, ou prenez-les dans votre vestiaire.',
  'scenewiz.lede.scenes':
    'Les scènes du Studio : votre mannequin et vos vêtements y prennent la place des siens, le lieu, la lumière, la pose et le cadrage restent identiques. Une photo par scène.',
  'scenewiz.lede.outfit':
    'Pour chaque scène, le styliste propose trois tenues avec vos pièces. Choisissez-en une, ou décrivez la vôtre.',
  'scenewiz.lede.finish': 'Facultatif — les réglages par défaut conviennent très bien.',
  'scenewiz.lede.go': 'Vérifiez, nommez-la si vous voulez, et lancez.',
  'scenewiz.need.scenes': 'Choisissez au moins une scène.',
  'scenewiz.need.outfit': 'Choisissez une tenue pour chaque scène.',
  'scenewiz.looksFailed': 'Le styliste n’a pas pu proposer de tenues pour cette scène.',
  'scenewiz.looksAgain': 'Réessayer',
  'scenewiz.otherHint': 'Ex. : la veste fermée, rentrée dans la jupe, sans le sac',
  'scenewiz.n.scene': '{n} scène',
  'scenewiz.n.scenes': '{n} scènes',
  'scenewiz.emptyStaff':
    'Aucune scène pour l’instant. Ajoutez-en dans La maison → Catalogue → Scènes, ou depuis la loupe d’une de vos images.',
  'scenewiz.qualityHint':
    'Chaque scène est retouchée en un seul message (la scène, votre mannequin, vos vêtements) : environ {cost} par photo.',
  'copy.eyebrow': 'Recréer une scène',
  'copy.go': 'Recréer la scène',
  'copy.noScenes': 'Le Studio n’a encore proposé aucune scène.',
  'copy.piecesHint':
    'Chaque pièce remplace celle portée de la même façon sur la scène ; ce qu’aucune ne remplace reste tel quel.',
  'brief.revised': 'Nouvelle version lancée : {name}.',
  'brief.reviseFailed': 'La nouvelle version n’a pas pu être lancée.',
  'roll.recreated': 'Photo recréée',
  'roll.recreatedKept': 'Même lieu que la photo d’origine.',
  'roll.recreatedMoved': 'Dans un autre lieu que la photo d’origine.',
  'roll.brief': 'La demande',
  'roll.previousVersion': '← Version précédente',
  'roll.reviseLabel': 'Ce n’est pas tout à fait ça ?',
  'roll.revisePlaceholder': 'Ex. : plus de mouvement, qu’elle sourie, un cadrage plus serré…',
  'roll.revise': 'Lancer une nouvelle version',
  'roll.revising': 'Le studio replanifie…',
  'roll.reviseWait': 'Attendez la fin du rendu pour demander une nouvelle version.',
  'group.fashion': 'Mode',
  'group.commercial': 'Commercial',
  'scene.customPlaceholder': 'Une rue pavée de Montmartre au petit matin, volets bleus, lumière douce…',
  'scene.photoHint':
    'La photo est respectée pour la lumière, la profondeur et la perspective. Les personnes qui y figurent sont exclues : seul le lieu est repris.',
  'error.no_models': 'Créez d’abord un mannequin : un shooting a besoin de quelqu’un à photographier.',
  'app.nav.shoot': 'Nouvelle séance',
  'app.nav.talent': 'Mannequins',
  'app.nav.rolls': 'Séances',
  'app.nav.sheet': 'Planche contact',
  'app.nav.account': 'Compte',
  'app.nav.admin': 'Administration',
  'app.signal': 'Services',
  'app.frames': 'Images ce mois-ci',
  'app.group.main': 'Principal',
  'app.group.archive': 'Archives',
  'app.group.account': 'Compte',
  'app.menu': 'Compte et réglages',
  'app.railOpen': 'Ouvrir le menu',
  'app.railClose': 'Fermer le menu',
  'search.title': 'Rechercher dans le studio',
  'search.placeholder': 'Chercher un visage, une séance, une section…',
  'search.none': 'Rien ici ne répond à « {query} ».',
  'search.group.sections': 'Aller à',
  'search.group.talent': 'Visages',
  'search.group.rolls': 'Séances',
  'app.signal.styling': 'Stylisme',
  'app.signal.analysis': 'Analyse',
  'app.signal.render': 'Rendu',
  'app.signal.live': 'actif',
  'app.signal.preview': 'aperçu',
  'app.signal.checking': 'vérification…',
  'app.theme': 'Apparence',
  'app.theme.dark': 'Sombre',
  'app.theme.light': 'Clair',
  'app.theme.system': 'Système',
  'app.language': 'Langue',

  /* ------------------------------ common ------------------------------- */
  'common.close': 'Fermer',
  'common.save': 'Enregistrer',
  'common.delete': 'Supprimer',
  'common.cancel': 'Annuler',
  'wiz.title': 'Nouvelle séance',
  'wiz.step.model': 'Mannequin',
  'wiz.step.clothes': 'Vêtements',
  'wiz.step.places': 'Lieux',
  'wiz.step.poses': 'Poses',
  'wiz.step.finish': 'Finitions',
  'wiz.step.go': 'Lancer',
  'wiz.title.model': 'Qui porte vos vêtements ?',
  'wiz.title.clothes': 'Que porte-t-elle ?',
  'wiz.title.places': 'Où ?',
  'wiz.title.poses': 'Dans quelles poses ?',
  'wiz.title.finish': 'Quelques finitions',
  'wiz.title.go': 'Prêt à shooter',
  'wiz.lede.model': 'Un mannequin du Studio, ou le vôtre.',
  'wiz.lede.clothes': 'Ajoutez les photos de vos pièces, ou prenez-les dans votre vestiaire.',
  'wiz.lede.places': 'Choisissez-en un ou plusieurs : chaque lieu multiplie les images.',
  'wiz.lede.poses':
    'Chaque pose est reproduite à l’identique, avec le visage de votre mannequin, vos vêtements et le lieu que vous choisirez ensuite.',
  'wiz.lede.finish': 'Facultatif — les réglages par défaut conviennent très bien.',
  'wiz.lede.go': 'Vérifiez, nommez-la si vous voulez, et lancez.',
  'wiz.need.model': 'Choisissez un mannequin.',
  'wiz.need.clothes': 'Ajoutez au moins une pièce.',
  'wiz.need.places': 'Choisissez au moins un lieu.',
  'wiz.need.poses': 'Choisissez au moins une pose.',
  'wiz.quality': 'Qualité des photos',
  'wiz.quality.plain': 'Standard',
  'wiz.quality.checked': 'Vérifiée',
  'wiz.qualityHint.plain':
    'Le moteur le plus fidèle, en trois étapes (visage, lieu, vêtements), sans vérification automatique : environ {cost} par photo. Refaites vous-même celles qui ne vous plaisent pas.',
  'wiz.engine.max': 'Fidélité maximale',
  'wiz.engine.standard': 'Économique',
  'wiz.engineHint.max': 'Le moteur le plus fidèle (Gemini Pro), en 2K.',
  'wiz.engineHint.standard':
    'Le moteur Flash récent (Nano Banana 2), en 1K : environ moitié prix, à essayer sur une séance avant de l’adopter.',
  'wiz.qualityHint.checked':
    'Dans chaque lieu, la première pose est la photo de référence, faite en un seul message (pose, mannequin, vêtements, lieu). Validez-la : les autres poses en sont déclinées dans la même conversation, sans toucher au mannequin, aux vêtements ni au lieu. Une image par photo : environ {cost}.',
  'wiz.n.pose': '{n} pose',
  'wiz.n.poses': '{n} poses',
  'wiz.n.place': '{n} lieu',
  'wiz.n.places': '{n} lieux',
  'wiz.back': 'Retour',
  'wiz.next': 'Suivant',
  'wiz.edit': 'Modifier',
  'wiz.name': 'Nom de la séance',
  'wiz.advanced': 'Composeur avancé (plans de caméra, réglages détaillés)',
  'wiz.shelf.mine': 'Les vôtres',
  'wiz.shelf.models': 'Modèles',
  'wiz.shelf.places': 'Lieux',
  'wiz.shelf.poses': 'Poses',
  'wiz.emptyStudioModelsStaff': 'Aucun modèle proposé pour l’instant. Ajoutez-en depuis Admin → Catalogue.',
  'wiz.emptyStudioPosesStaff': 'Aucune pose proposée pour l’instant. Ajoutez-en depuis Admin → Catalogue.',
  'wiz.kind.all': 'Tous',
  'wiz.kind.street': 'Rues',
  'wiz.kind.studio': 'Fonds unis',
  'wiz.studio': 'Studio',
  'wiz.location': 'En extérieur',
  'wiz.studioSet': 'Plateau studio',
  'wiz.addModel': 'Créer votre mannequin',
  'wiz.addPlace': 'Ajouter un lieu',
  'wiz.addPose': 'Ajouter des poses',
  'wiz.addPoseHint': 'Une pose par photo',
  'wiz.poseAdding': 'Ajout ({n})…',
  'wiz.poseFailed': 'Cette photo n’a pas pu être ajoutée comme pose.',
  'wiz.placeFailed': 'Ce lieu n’a pas pu être enregistré.',
  'wiz.placePhoto': 'Une photo du lieu (facultatif)',
  'wiz.placeName': 'Nom',
  'wiz.placeNamePlaceholder': 'ex. Notre showroom',
  'wiz.placeDesc': 'Description',
  'wiz.placeDescPlaceholder': 'ex. un loft blanc avec de grandes fenêtres et un sol en béton',
  'wiz.placeSave': 'Enregistrer le lieu',
  'wiz.removePlace': 'Supprimer {name}',
  'wiz.removePose': 'Supprimer {name}',
  'wiz.removeBody': 'Il est retiré de la bibliothèque définitivement.',
  'wiz.removeGo': 'Supprimer',
  'wiz.rename': 'Renommer',
  'wiz.renameNamed': 'Renommer {name}',
  'wiz.renameFailed': 'Ce nom n’a pas pu être enregistré.',
  'wiz.emptyStudioModels': 'Aucun modèle proposé pour l’instant. Créez le vôtre dans « Les vôtres ».',
  'wiz.emptyMyPlaces': 'Pas encore de lieu à vous.',
  'wiz.emptyStudioPoses':
    'Aucune pose proposée pour l’instant. Ajoutez une photo de pose dans « Les vôtres ».',
  'wiz.emptyMyPoses': 'Pas encore de pose à vous : ajoutez la photo d’une personne dans la pose voulue.',
  'wiz.light': 'Lumière',
  'wiz.lightFrom.place': 'Celle du lieu',
  'wiz.lightFrom.pose': 'Celle de la photo de pose',
  'wiz.lightFrom.chosen': 'En choisir une',
  'wiz.asInPose': 'Comme sur la pose',
  'wiz.severalExpr': 'Chaque expression choisie est photographiée sur chaque image : la séance se multiplie.',
  'sheet.previous': 'Image précédente',
  'sheet.next': 'Image suivante',
  'sheet.discardTitle': 'Abandonner ce mannequin ?',
  'wardrobe.confirmDeleteTitle': 'Supprimer {label} ?',
  'shoot.recipeNameLabel': 'Nom',
  'common.ok': 'OK',
  'common.deleteIt': 'Supprimer',
  'common.discardIt': 'Abandonner',
  'common.back': 'Toutes les séances',
  'common.download': 'Télécharger l’image',
  'common.frames': 'images',
  'common.frame': 'image',
  'common.keeper': 'sélection',
  'common.keepers': 'sélections',
  'common.setUp': 'cadrage',
  'common.setUps': 'cadrages',
  'sheet.discardConfirm': 'Abandonner ce modèle ? Tout ce que vous avez saisi sera perdu.',

  /* ----------------------------- new shoot ----------------------------- */
  'shoot.eyebrow': 'Feuille de service',
  'shoot.group.subject': 'Le sujet',
  'shoot.group.frames': 'Les plans',
  'shoot.group.look': 'Le look',
  'shoot.who': 'Qui le porte',
  'shoot.what': 'Ce qu’il ou elle porte',
  'shoot.styling': 'Stylisme',
  'enum.styling.as-uploaded': 'Uniquement ces pièces',
  'enum.styling.complete': 'Compléter la tenue',
  'shoot.stylingUploadedHint': 'Le styliste habille le mannequin avec les pièces ci-dessus, rien d’autre.',
  'shoot.stylingCompleteHint':
    'Donnez une pièce et le styliste ajoute ce qui manque — un bas, des chaussures, une couche, des accessoires — choisis pour l’accompagner et pour le lieu. Tout ce qui est ajouté est indiqué sur la séance.',
  'shoot.accessories': 'Accessoires',
  'shoot.accessoriesOther': 'Autres accessoires',
  'shoot.accessoriesPlaceholder': 'une chevalière en argent, des lunettes de soleil écaille…',
  'shoot.accessoriesHint':
    'Ajoutés en mots, sans photo de référence — précisez ce qui compte, le métal ou la couleur.',
  'shoot.expression': 'Expression',
  'shoot.expressionHint':
    'Indépendante de la pose : une pose catalogue peut être photographiée avec un sourire.',
  'enum.expression.auto': 'Selon la pose',
  'enum.expression.neutral': 'Sans sourire',
  'enum.expression.hint': 'Esquisse',
  'enum.expression.soft': 'Sourire discret',
  'enum.expression.open': 'Sourire franc',
  'enum.expression.laugh': 'Rire',
  'enum.expression.talking': 'En conversation',
  'scene.auto': 'Au choix du styliste',
  'scene.autoDesc': 'Choisi pour aller avec la tenue, et indiqué sur la séance.',
  'scene.stylist': 'styliste',
  'scene.paris': 'Paris',
  'group.lifestyle': 'Lifestyle',
  'roll.stylistAdded': 'Ajouté en mots, sans photo : {items}.',
  'shoot.attitude': 'Attitude',
  'enum.attitude.editorial': 'Éditorial',
  'enum.attitude.influencer': 'Instagrameuse',
  'enum.attitude.relaxed': 'Décontractée',
  'shoot.attitudeHint.editorial': 'Posée et chic, comme un lookbook.',
  'shoot.attitudeHint.influencer':
    'Spontanée, un brin joueuse, comme prise par une amie — et une tenue du jour plutôt qu’un éditorial.',
  'shoot.attitudeHint.relaxed':
    'Simple et sans chichis, et une tenue moins habillée : décontractée plutôt que chic.',
  'shoot.fidelity': 'Fidélité aux pièces',
  'enum.fidelity.standard': 'Standard',
  'enum.fidelity.max': 'Maximale',
  'shoot.fidelityStandardHint': 'Le moteur rapide. Suffisant pour la plupart des séances.',
  'shoot.fidelityMaxHint':
    'Le moteur le plus fort, en résolution double, avec la description gardée mot pour mot dans chaque plan. Tient bien mieux les longueurs, le nombre de boutons et les broderies — plus lent, et plusieurs fois plus cher par image.',
  'wardrobe.dropHere': 'Déposez les photos ici',
  'wardrobe.orDrag': 'ou glissez-les ici',
  'wardrobe.notAnImage': 'Seules les photos PNG, JPEG ou WebP peuvent être ajoutées.',
  'wardrobe.detailHint':
    'Cette pièce a des détails fins — ajoutez-en une photo rapprochée (« + Détail » ci-dessous) pour que le rendu puisse les copier.',
  'group.influencer': 'Influenceuse',
  'group.editorial': 'Éditorial',
  'shoot.optional': 'facultatif',
  'shoot.setUpsN': '{n} cadrages',
  'shoot.setUpN': '{n} cadrage',
  'shoot.takesPerSetUp': 'Prises par cadrage',
  'shoot.severalHint': 'Chaque attitude choisie est photographiée sur chaque image : la séance se multiplie.',
  'plan.n.models': '{n} mannequins',
  'plan.n.angle': '{n} plan',
  'plan.n.angles': '{n} plans',
  'plan.n.positions': '{n} positions',
  'plan.n.scenes': '{n} décors',
  'plan.n.attitudes': '{n} attitudes',
  'plan.n.expressions': '{n} expressions',
  'shoot.positionsDefault': 'Chaque cadrage garde la sienne',
  'shoot.createModel': 'Créer un mannequin',
  'shoot.showTechnical': 'Afficher les réglages techniques',
  'shoot.hideTechnical': 'Masquer les réglages techniques',
  'shoot.longRun': 'Séance longue — comptez un moment.',
  'shoot.notesHint':
    'Nommez ce dont vous n’avez pas de photo, et dites comment les pièces se superposent. Des phrases simples suffisent : c’est lu comme des consignes.',
  'plan.empty': 'Choisissez quelqu’un et un cadrage — la séance apparaît ici.',
  'plan.andMore': '+ {n} images de plus',
  'plan.someone': 'Quelqu’un',
  'shoot.step.positions': 'Positions',
  'shoot.wardrobeHint':
    'À plat, sur cintre ou en détouré, tout fonctionne. Un vêtement par photo ; l’ordre des couches fixe la numérotation à laquelle le prompt renvoie — IMG 01, IMG 02 — donc la pièce la plus extérieure vient en premier.',
  'shoot.notes': 'Notes pour le styliste',
  'shoot.where': 'Où',
  'shoot.ownBackground': 'Votre propre décor',
  'shoot.light': 'Lumière',
  'shoot.colour': 'Couleur',
  'shoot.filmGrain': 'Pellicule & grain',
  'shoot.grain': 'Grain',
  'shoot.vignette': 'Vignettage',
  'shoot.aperture': 'Profondeur de champ',
  'shoot.apertureHint':
    'Dite au rendu : lui seul sait ce qui est près d’elle et ce qui est loin. La pellicule, le grain et le vignettage se règlent après, sur chaque photo.',
  'common.auto': 'Auto',
  'shoot.noGrain': 'Sans grain',
  'shoot.grainValue': 'Grain {n}',
  'shoot.takesCount': 'Combien de prises',
  'shoot.grainNote':
    'Le grain est ajouté après le rendu, pas demandé au rendu — il est donc identique sur chaque image, et n’importe laquelle peut être redéveloppée ensuite sans repayer une prise.',
  'shoot.shape': 'Forme',
  'shoot.loadingStudio': 'Ouverture du studio…',
  'shoot.recipes': 'Style maison',
  'shoot.recipeSave': 'Enregistrer ces réglages',
  'shoot.recipeName': 'Nommez ce réglage',
  'shoot.recipeDefault': 'Style maison',
  'shoot.recipeSaved': '« {name} » enregistré. Les vêtements n’en font pas partie.',
  'shoot.recipeApplied': '« {name} » appliqué — ajoutez les vêtements.',
  'shoot.recipeAppliedPartial': '« {name} » appliqué, sans un mannequin qui n’existe plus.',
  'shoot.recipeForget': 'Oublier {name}',
  'shoot.noModels': 'Personne au fichier pour l’instant.',
  'shoot.notesPlaceholder': 'ex. un débardeur écru sous le gilet, des boucles dorées, l’ourlet avant rentré',
  'shoot.copySpace': 'Place pour le texte',
  'shoot.copy.none': 'Aucune — image pleine',
  'shoot.copy.left': 'Espace libre à gauche',
  'shoot.copy.right': 'Espace libre à droite',
  'shoot.copy.top': 'Espace libre en haut',
  'shoot.copyHint':
    'Pour les bannières et les pages de campagne : le sujet se décale d’un côté et l’autre tiers reste libre, pour y poser un titre.',
  'shoot.styleNotes': 'Notes de style',
  'shoot.styleNotesPlaceholder': 'e-commerce sobre, sans stylisation',
  'shoot.untitledRoll': 'Séance',
  'shoot.name': 'Nom de la séance',
  'shoot.go': 'Lancer la séance',
  'shoot.going': 'En cours…',
  'shoot.blocked.limit': 'Au-delà du plafond de {n} images fixé sur ce serveur — réduisez.',
  'shoot.blocked.model': 'Choisissez un modèle pour continuer.',
  'shoot.blocked.garments': 'Ajoutez au moins une photo de vêtement.',
  'shoot.blocked.povs': 'Choisissez au moins un cadrage.',
  'shoot.failed': 'Impossible de lancer la séance.',
  'shoot.rolling': 'En cours — {n} images pour « {name} »',
  'shoot.rollingOne': 'En cours — 1 image pour « {name} »',

  'pose.search': 'Chercher une position — allongée, marche, mains…',
  'pose.searchAria': 'Chercher une position',
  'pose.needsSupport': 'demande un appui',
  'posture.standing': 'debout',
  'posture.walking': 'en marche',
  'posture.seated': 'assise',
  'posture.reclining': 'allongée',
  'posture.crouching': 'accroupie',
  'posture.leaning': 'appuyée',
  'scene.bringsLight': 'apporte sa lumière',
  'scene.studio': 'studio',
  'scene.custom': 'Personnalisé',
  'scene.describe': 'Décrivez votre décor',
  'scene.removePhoto': 'Retirer la photo de décor',
  'wardrobe.what': 'Qu’est-ce que c’est ? ex. gilet à sequins',
  'wardrobe.notes': 'Ce que la photo rend mal (facultatif)',
  'wardrobe.notesShort': 'Note (facultatif)',
  'wardrobe.kind': 'Type',
  'wardrobe.layer': 'Couche',
  'wardrobe.detailHintShort':
    'Détails fins : ajoutez une ou plusieurs photos rapprochées (zip, manche, motif…).',

  'shoot.castOf': '{done}/{total} retenus',
  'shoot.portraitOnFile': 'portrait au fichier',
  'shoot.noPortrait': 'pas de portrait',
  'shoot.noPortraitWarning':
    '{names} n’a pas de portrait. Sans portrait le visage dérive d’une image à l’autre — générez-en un depuis la page Casting.',
  'wardrobe.add': 'Ajouter des photos de vêtements',
  'wardrobe.photograph': 'Photographier',
  'capture.heading': 'À refaire peut-être : {n} de ces photos risquent de vous coûter des rendus.',
  'capture.soft': 'semble floue — restez immobile, touchez le vêtement pour faire la mise au point',
  'capture.dark': 'très sombre — approchez-vous d’une fenêtre ou allumez une lampe',
  'capture.bright': 'surexposée — coupez le flash, ou sortez du soleil direct',
  'capture.small': 'assez petite — rapprochez-vous plutôt que de recadrer après',
  'capture.dismiss': 'Compris',
  'wardrobe.uploading': 'Import…',
  'voice.dictate': 'Dicter',
  'voice.stop': 'Arrêter',

  'filter.all': 'Tout',
  'filter.none': 'Aucun',
  'group.full-body': 'Plein pied',
  'group.portrait': 'Portrait & recadrages',
  'group.detail': 'Détails',
  'group.dynamic': 'Mouvement',
  'group.angle': 'Angles',
  'group.static': 'Debout',
  'group.movement': 'Mouvement',
  'group.ground': 'Au sol & assise',
  'group.prop': 'En appui',
  'group.gesture': 'Geste & regard',
  'scene.onLocation': 'En extérieur',
  'scene.studioSweep': 'Fond de studio',
  'scene.fromPhoto': 'D’après votre photo',
  'scene.describeOrUpload': 'Décrivez-le, ou importez une photo',
  'scene.uploadPhoto': 'Importer une photo de décor',
  'scene.replacePhoto': 'Remplacer la photo',
  'scene.uploadFailed': 'Impossible d’importer cette image.',
  'shoot.selectedOf': '{done}/{total} sélectionnés',
  'wardrobe.remove': 'Retirer {label}',
  'wardrobe.fromWardrobe': 'Depuis le vestiaire',
  'wardrobe.hideWardrobe': 'Masquer le vestiaire',
  'wardrobe.loading': 'Ouverture du vestiaire…',
  'wardrobe.empty': 'Rien en réserve — envoyez une pièce et elle restera ici.',
  'wardrobe.analysed': 'analysée',
  'wardrobe.takeOff': 'Retirer',
  'wardrobe.deleteForever': 'Supprimer {label} du vestiaire',
  'wardrobe.confirmDelete':
    'Supprimer {label} définitivement du vestiaire ? Les séances qui l’ont utilisée gardent leurs images.',
  'wardrobe.saveFailed': 'Impossible d’enregistrer ce changement.',
  'wardrobe.view.front': 'devant',
  'wardrobe.view.back': 'dos',
  'wardrobe.view.side': 'côté',
  'wardrobe.view.detail': 'détail',
  'wardrobe.view.label': 'étiquette',
  'wardrobe.exampleTag': 'Exemple',
  'wardrobe.example.front': 'Une pièce par photo, de face, à plat ou sur cintre, sur fond uni.',
  'wardrobe.example.back': 'Le dos, à plat ou sur cintre, comme la face.',
  'wardrobe.example.side': 'De profil, pour la coupe et la couture de côté.',
  'wardrobe.example.detail': 'Un gros plan : zip, couture, motif, broderie…',
  'wardrobe.example.label': 'L’étiquette, nette et lisible.',
  'wardrobe.removeView': 'Retirer la photo {kind}',
  'wardrobe.details': 'Détails',
  'wardrobe.detailWhere': 'Où ? (zip, manche…)',
  'wardrobe.detailWhereN': 'Emplacement du détail {n}',
  'wardrobe.layer1': 'à même la peau',
  'wardrobe.layer5': 'couche intermédiaire',
  'wardrobe.layer10': 'couche extérieure',
  'wardrobe.layer12': 'par-dessus tout',
  'dress.look': 'Le look',
  'dress.addPiece': 'Ajouter une pièce',
  'dress.newPiece': 'Nouvelle pièce',
  'dress.newKind': 'Nouvelle pièce : {kind}',
  'dress.changeKind': 'Changer de type',
  'dress.the.kind': 'quel type ?',
  'dress.hint.kind': 'Choisissez d’abord le type : l’exemple à suivre et la couche s’y adaptent.',
  'dress.viewOf': '{n} vue sur {of}',
  'dress.viewsOf': '{n} vues sur {of}',
  'dress.complete': 'Complète',
  'dress.views': 'Vues de la pièce',
  'dress.removePiece': 'Retirer cette pièce',
  'dress.added': 'Ajoutée',
  'dress.skip': 'Passer',
  'dress.done': 'Pièce terminée',
  'dress.addDetail': 'Ajouter un gros plan',
  'dress.closet': 'Votre vestiaire',
  'dress.closetDone': 'Terminé',
  'dress.dropOrPick': 'Glissez-la ici, ou touchez pour choisir',
  'dress.dropOrPickMany': 'Glissez-en une ou plusieurs ici, ou touchez pour choisir',
  'dress.view.front': 'Face',
  'dress.view.back': 'Dos',
  'dress.view.side': 'Profil',
  'dress.view.detail': 'Détails',
  'dress.view.label': 'Étiquette',
  'dress.view.ghost': 'Ghost',
  'dress.the.ghost': 'les photos ghost',
  'dress.side.left': 'Profil gauche',
  'dress.side.right': 'Profil droit',
  'dress.yours.side.left': 'Votre photo du profil gauche',
  'dress.yours.side.right': 'Votre photo du profil droit',
  'dress.hint.ghost':
    'Les séances partent de ces photos : la pièce seule, sans mannequin, cintre ni autre vêtement.',
  'dress.ghostRedo': 'Tout refaire · {cost}',
  'dress.ghostRetouch': 'Refaire',
  'dress.ghostWhat': 'ex. la broderie de la manche gauche est trop petite (facultatif)',
  'dress.ghostWhatLabel': 'Refaire la photo ghost « {side} » — que corriger ?',
  'dress.ghostRetouchGo': 'Refaire · {cost}',
  'dress.ghostMaking': 'En préparation…',
  'dress.ghostWaiting': 'Pas encore faite',
  'dress.ghostFailed': 'Les photos ghost n’ont pas pu être créées.',
  'dress.the.front': 'la face',
  'dress.the.back': 'le dos',
  'dress.the.side': 'le profil',
  'dress.the.detail': 'les détails',
  'dress.the.label': 'l’étiquette',
  'dress.yours.new': 'Votre photo de face',
  'dress.yours.back': 'Votre photo du dos',
  'dress.yours.side': 'Votre photo de profil',
  'dress.yours.detail': 'Vos gros plans',
  'dress.yours.label': 'Votre photo de l’étiquette',
  'dress.hint.new':
    'Seule la face est obligatoire ; le dos, le profil et les gros plans rendent la pièce plus fidèle.',
  'dress.hint.front': 'Le nom, le type et la couche indiquent au studio comment habiller le mannequin.',
  'dress.hint.back': 'Sans le dos, le studio l’imaginera à partir de la face.',
  'dress.hint.side':
    'Un profil suffit, les deux c’est mieux. Sans profil, le studio déduira la coupe de la face et du dos.',
  'dress.hint.detail': 'Les gros plans gardent motifs, zips et broderies fidèles. Jusqu’à 6.',
  'dress.hint.label': 'Lue une fois pour la matière, elle n’apparaît jamais sur les photos.',
  'voice.listening': 'À l’écoute — parlez normalement, puis appuyez sur Arrêter.',

  'picker.available': '{n} disponibles',
  'picker.chosen': '{n} choisies · photographiées dans cet ordre',
  'picker.noMatch': 'Aucune position ne correspond à « {query} ».',
  'pov.crop': 'serré',
  'pov.full': 'entier',

  /* ------------------------------ talent ------------------------------- */
  'talent.eyebrow': 'Casting',
  'talent.title': 'Modèles',
  'talent.lede':
    'Un modèle est un visage que vous réutilisez d’une séance à l’autre. Son portrait est joint à chaque rendu : c’est ce qui garde la même personne sur toutes les images.',
  'talent.loadFailed': 'Impossible de charger le casting.',
  'talent.new': 'Nouveau modèle',
  'talent.display': 'Casting',
  'talent.newCard': 'Nouveau',
  'talent.newCardHint': 'Décrivez-le ; le studio fait la face, le profil et le dos.',
  'talent.find': 'Chercher un visage',
  'talent.noneMatch': 'Aucun visage ne correspond.',
  'talent.only.all': 'Tout le monde',
  'talent.only.ready': 'Avec portrait',
  'talent.only.blank': 'Sans portrait',
  'talent.consistency': 'Score d’identité moyen sur les images terminées',
  'talent.empty': 'Personne au fichier.',
  'talent.emptyHint':
    'Décrivez quelqu’un en une phrase et le studio le conçoit et le photographie, ou remplissez les détails vous-même.',
  'talent.emptyCta': 'Choisir votre premier modèle',
  'talent.source.manual': 'Studio · maison',
  'talent.source.designed': 'Studio · conçu',
  'talent.source.uploaded': 'Studio · d’après référence',
  'talent.pending': '{n} prises à juger',
  'talent.pendingOne': '1 prise à juger',
  'talent.health.bad': 'Aucun visage au fichier',
  'talent.health.badHint': 'Chaque rendu en invente un.',
  'talent.health.warn': 'Un seul angle',
  'talent.health.warnHint': 'Les profils et les rotations peuvent dériver.',
  'talent.health.ok': 'Verrouillé',
  'talent.health.okHint': '{n} angles au fichier.',
  'talent.edit': 'Modifier {name}',

  /* --------------------------- model editor ---------------------------- */
  'caster.new': 'Choisir un modèle',
  'caster.newSub': 'Un visage réutilisé d’une séance à l’autre. La ligne d’identité ancre chaque image.',
  'caster.editSub': 'Faites des prises jusqu’au bon visage, puis gardez celle que vous voulez.',
  'caster.noPortrait': 'Pas encore de portrait',
  'caster.shooting': 'Prise en cours…',
  'caster.shootTakes': 'Faire {n} prises',
  'caster.shootTake': 'Faire 1 prise',
  'caster.shootPortrait': 'Faire un portrait',
  'caster.anotherAngle': 'Un autre angle d’elle',
  'caster.thin':
    'Décrivez d’abord le modèle — un brief ci-dessus, ou les cheveux et l’origine ci-dessous. Depuis un formulaire vide, le portrait n’est qu’« une femme », et vous obtenez une inconnue différente à chaque fois.',
  'caster.fromFile':
    'Photographiée d’après les portraits déjà au fichier : c’est la même personne sous un autre angle, pas une inconnue qui correspond aux mots.',
  'caster.fromLine': 'Photographiée d’après la ligne d’identité, sur un fond gris uni.',
  'caster.onFile': 'Au fichier',
  'caster.primaryHint': 'Le premier est le visage affiché partout ; appuyez sur un autre pour le promouvoir.',
  'caster.makePrimary': 'Définir comme portrait principal',
  'caster.isPrimary': 'Portrait principal',
  'caster.removePortrait': 'Retirer ce portrait',
  'caster.upload': 'Importer le vôtre',
  'caster.turnaround': 'Vue 360',
  'caster.turnaroundHint':
    'Trois photos en pied — face, profil, dos — pour garder sa silhouette, ses proportions et ses cheveux sous tous les angles. Chaque image reçoit celle de son angle.',
  'caster.view.front': 'Face',
  'caster.view.profile': 'Profil',
  'caster.view.back': 'Dos',
  'caster.view.face': 'Visage',
  'caster.shoot360': 'Faire le portrait 360°',
  'caster.redo360': 'Refaire le 360°',
  'caster.make360': 'Faire le 360°',
  'caster.step.portrait': 'Le visage…',
  'caster.step.views': 'Face, profil, dos…',
  'caster.describe': 'Description',
  'caster.newTakes': 'Nouvelles prises du visage',
  'caster.redrawSide': 'Redessiner : {side}',
  'caster.thinShort': 'Décrivez-le d’abord : un brief, ou les cheveux et l’origine.',
  'caster.newTitle': 'Nouveau mannequin',
  'caster.go': 'Créer',
  'caster.removeView': 'Retirer la vue {side}',
  'caster.consentFirst': 'Cochez d’abord la case d’autorisation ci-dessus.',
  'caster.manualHint':
    'Ajoutez la photo de son visage, puis générez sa vue 360 en body beige — ou importez vous-même face, profil et dos. Rien n’est lu sur sa photo ni écrit pour elle.',
  'caster.addPhoto': 'Ajouter',
  'caster.make360Beige': 'Générer sa vue 360 en body beige',
  'caster.redo360Beige': 'Regénérer sa vue 360',
  'caster.addSlot': 'Ajouter la photo : {side}',
  'caster.replaceSlot': 'Remplacer la photo : {side}',
  'caster.needFace': 'Ajoutez une photo de son visage.',
  'caster.drawViews': 'Générer les vues manquantes',
  'caster.drawingViews': 'Rendu des vues…',
  'caster.drawViewsHint':
    'Dessinées à partir de son portrait, en tenue neutre sur fond gris. Chaque vue compte comme une image.',
  'caster.drawViewsNeedsFace':
    'Ajoutez d’abord un portrait : les vues sont dessinées à partir de son visage.',
  'caster.drawViewsNeedsSave': 'Importez ses photos ici, ou enregistrez le mannequin pour générer ses vues.',
  'caster.errViews': 'Les vues n’ont pas pu être générées.',
  'caster.consent': 'J’ai le droit d’utiliser l’image de cette personne, et elle est majeure.',
  'error.consent_required': 'Confirmez que vous avez le droit d’utiliser cette image avant d’envoyer.',
  'error.minor': 'Ce studio ne produit que des adultes, et cette photo n’en montre pas clairement un.',
  'error.explicit': 'Cette photo ne peut pas être utilisée ici.',
  'error.no_person': 'Cette photo ne montre pas de visage.',
  'caster.reading': 'Lecture du visage…',
  'caster.readFromPhoto':
    'Description lue depuis votre photo, pour que les mots et le visage concordent. Chaque prise correspond à cette photographie.',
  'caster.takes': 'Prises · choisissez-en une',
  'caster.takesOnTable': '{n} sur la table',
  'caster.takesHint':
    'Rien ici n’est encore rattaché à une séance. Gardez celle que vous voulez comme portrait ; jetez le reste.',
  'caster.keep': 'Garder',
  'caster.discard': 'Jeter',
  'caster.keepAria': 'Garder cette prise',
  'caster.discardAria': 'Jeter cette prise',
  'caster.brief': 'Partir d’un brief',
  'caster.briefPlaceholder': 'Femme scandinave, fin vingtaine, coupe courte platine',
  'caster.design': 'Concevoir & photographier',
  'caster.designing': 'Conception…',
  'caster.designHint': 'Remplit tout ci-dessous et fait un portrait assorti.',
  'caster.name': 'Nom',
  'caster.unset': 'Non précisé',
  'caster.ethnicityPlaceholder': 'Ex. : est-asiatique',
  'caster.featuresPlaceholder': 'Ex. : taches de rousseur',
  /* ------------------------------ enumerations -------------------------- *
   * Les ensembles fermés que le serveur stocke en anglais. Les silhouettes
   * s'accordent avec « silhouette », qui est le nom du champ, et les états
   * avec « séance » : les deux sont féminins, donc « Droite », « Terminée ».
   * --------------------------------------------------------------------- */
  'enum.gender.female': 'Femme',
  'enum.gender.male': 'Homme',
  'enum.gender.non-binary': 'Non-binaire',
  'enum.build.slim': 'Mince',
  'enum.build.athletic': 'Athlétique',
  'enum.build.straight': 'Droite',
  'enum.build.curvy': 'Ronde',
  'enum.build.plus': 'Grande taille',
  'enum.build.muscular': 'Musclée',
  'enum.status.draft': 'Brouillon',
  'enum.status.queued': 'En attente',
  'enum.status.analyzing': 'Lecture des pièces',
  'enum.status.styling': 'Stylisme',
  'enum.status.rendering': 'Rendu',
  'enum.status.reviewing': 'Contrôle',
  'enum.status.complete': 'Terminée',
  'enum.status.failed': 'Échec',
  'enum.status.cancelled': 'Annulée',
  'enum.category.top': 'Haut',
  'enum.category.outerwear': 'Veste ou manteau',
  'enum.category.bottom': 'Bas',
  'enum.category.dress': 'Robe',
  'enum.category.shoes': 'Chaussures',
  'enum.category.bag': 'Sac',
  'enum.category.accessory': 'Accessoire',
  'enum.category.headwear': 'Chapeau',

  'caster.groupWho': 'Qui est-ce',
  'caster.groupColouring': 'Teint et yeux',
  'caster.groupHair': 'Cheveux',
  'caster.groupDetails': 'Autres détails',
  'caster.gender': 'Genre',
  'caster.build': 'Silhouette',
  'caster.age': 'Tranche d’âge',
  'caster.height': 'Taille',
  'caster.ethnicity': 'Origine',
  'caster.skinTone': 'Carnation',
  'caster.hairColour': 'Couleur des cheveux',
  'caster.hairLength': 'Longueur des cheveux',
  'caster.hairStyle': 'Coiffure',
  'caster.eyeColour': 'Couleur des yeux',
  'caster.makeup': 'Maquillage',
  'caster.features': 'Signes particuliers',
  'caster.identity': 'Ligne d’identité',
  'caster.rebuild': 'Reconstruire depuis le formulaire',
  'caster.identityTouched': 'Modifiée à la main — les champs ci-dessus ne la changent plus.',
  'caster.identityDerived':
    'Construite depuis les champs ci-dessus. Modifiez-la et elle cesse de les suivre.',
  'caster.savingAs': 'Enregistrement sous « {name} »',
  'caster.unsaved': 'Modifications non enregistrées',
  'caster.upToDate': 'À jour',
  'caster.needName': 'Donnez un nom au modèle pour enregistrer.',
  'caster.saveNew': 'Ajouter au fichier',
  'caster.saveEdit': 'Enregistrer',
  'caster.saving': 'Enregistrement…',
  'caster.saved': '« {name} » enregistrée',
  'caster.added': '{name} est au fichier',
  'caster.removed': '{name} est retirée du fichier',
  'caster.cast': 'Retenue — cette prise est désormais son portrait',
  'caster.errNoDesign': 'Impossible de concevoir un modèle.',
  'caster.errNoPortrait': 'Impossible de faire un portrait.',
  'caster.errNoKeep': 'Impossible de garder cette prise.',
  'caster.errNoDiscard': 'Impossible de jeter cette prise.',
  'caster.errNoReorder': 'Impossible de réordonner.',
  'caster.errNoSave': 'Impossible d’enregistrer.',
  'caster.errNoDelete': 'Impossible de supprimer.',
  'caster.errPartial': '{done} sur {total} sont revenues — {message}',
  'caster.errUpload': 'Échec de l’import.',

  /* ------------------------------- rolls ------------------------------- */
  'rolls.eyebrow': 'Archives',
  'rolls.title': 'Séances',
  'rolls.lede': 'Toutes les séances que vous avez photographiées.',
  'rolls.only.all': 'Toutes',
  'rolls.only.running': 'En cours',
  'rolls.only.done': 'Terminées',
  'rolls.count': '{n} séances',
  'rolls.countOne': '1 séance',
  'rolls.column': 'Images',
  'rolls.new': 'Nouvelle séance',
  'rolls.empty': 'Rien de photographié pour l’instant.',
  'rolls.emptyCta': 'Préparer votre première séance',
  'rolls.deleted': '« {name} » supprimée',
  'rolls.delete': 'Supprimer {name}',
  'rolls.confirmDelete': 'Supprimer « {name} » ?',
  'rolls.confirmDeleteBody':
    'Les {n} images de cette séance partent avec elle, et elles ont été payées. C’est sans retour.',
  'rolls.status.rolling': 'en cours',
  'rolls.display': 'Vos séances',
  'rolls.mode.copy': 'Recréer',
  'rolls.mode.presets': 'Composer',
  'rolls.mode.brief': 'Décrire',
  'rolls.mode.scene': 'Scène',
  'rolls.mode.poses': 'Poses',
  'rolls.kept': '{n} gardées',
  'rolls.keptOne': '1 gardée',
  'rolls.open': 'Ouvrir {name}',

  /* ------------------------------ one roll ----------------------------- */
  'roll.cut': 'Couper',
  'roll.saveRecipe': 'Enregistrer comme style maison',
  'roll.working': 'En cours…',
  'roll.failed': 'Cette séance a échoué.',
  'roll.look': 'Le look',
  'roll.continuity': 'Sur la série',
  'roll.drift.major': 'Important',
  'roll.drift.minor': 'Léger',
  'roll.driftFrame': 'Image {n}',
  'loupe.backToCurrent': 'Revenir à l’actuelle',
  'loupe.takeCurrentShort': 'Actuelle',
  'loupe.downloadOriginal': 'Télécharger',
  'loupe.downloadPrint': 'Pour l’impression (2×)',
  'loupe.downloadHint':
    'Pour l’impression : deux fois plus grande et plus nette, pour un catalogue ou un tirage. Aucun détail n’est inventé.',
  'loupe.toFix': 'Ce que le contrôle a relevé',
  'loupe.takesOne': '1 prise',
  'loupe.takesN': '{n} prises',
  'roll.driftCount': '{n} images à reprendre',
  'roll.driftCountOne': '1 image à reprendre',
  'roll.log': 'Journal du studio',
  'roll.frames': 'Images',
  'roll.setUps': 'Cadrages',
  'roll.format': 'Format',
  'roll.notLoaded': 'Impossible de charger cette séance.',
  'roll.inspect': 'Examiner la image {n}',
  'roll.queuedFrame': 'En file',
  'roll.checkingFrame': 'Contrôle qualité',
  'roll.ready': 'images prêtes',
  'roll.dockDone': '{n} images prêtes',
  'roll.developingFrame': 'Développement…',
  'roll.frameFailed': 'Échec',
  'roll.referenceBadge': 'Référence',
  'roll.referenceApprovedBadge': 'Référence validée',
  'roll.referenceTitle': 'Validez la photo de référence de chaque lieu',
  'roll.referenceHint':
    'Quand le mannequin et le vêtement vous conviennent, validez-la : les {n} autres poses en seront déclinées, sans toucher au mannequin, aux vêtements ni au lieu. Sinon, corrigez-la (par exemple « le vêtement n’est pas fidèle, retravaille-le ») ou refaites-la.',
  'roll.referenceHintOne':
    'Quand le mannequin et le vêtement vous conviennent, validez-la : l’autre pose en sera déclinée, sans toucher au mannequin, aux vêtements ni au lieu. Sinon, corrigez-la (par exemple « le vêtement n’est pas fidèle, retravaille-le ») ou refaites-la.',
  'roll.approveReference': 'Valider et décliner les {n} poses',
  'roll.approveReferenceOne': 'Valider et décliner l’autre pose',
  'roll.approving': 'Validation…',
  'roll.approveNotReady':
    'La référence n’est pas encore prête : attendez-la, ou gardez l’une de ses versions.',
  'roll.referenceApproved': 'Référence validée — les autres poses sont en cours.',
  'roll.approveError': 'Impossible de valider cette référence.',
  'roll.waitsReference': 'Attend la référence',
  'roll.markError': 'Impossible d’enregistrer cette annotation.',
  'roll.updated': 'Vue mise à jour',

  /* ------------------------------- loupe ------------------------------- */
  'loupe.render': 'Rendu',
  'loupe.reference': 'Référence',
  'loupe.frameOnly': 'L’image seule',
  'loupe.history': 'Historique',
  'loupe.historyCount': '{n} versions',
  'loupe.takeCurrent': 'Sur la planche',
  'loupe.take.render': 'Première prise',
  'loupe.take.retry': 'Reprise automatique',
  'loupe.take.repair': 'Réparation de cohérence',
  'loupe.take.reshoot': 'Refaite',
  'loupe.take.variation': 'Variante',
  'loupe.takeViewing':
    'Une version précédente — {origin}, {time}. La planche affiche toujours la version actuelle.',
  'loupe.restoreTake': 'Utiliser cette version',
  'loupe.restoreFailed': 'Impossible de remettre cette version.',
  'loupe.wipe': 'Volet entre le rendu et la référence',
  'loupe.exposure': 'Prise de vue',
  'loupe.exp.setUp': 'Cadrage',
  'loupe.exp.frame': 'Vue',
  'loupe.exp.pose': 'Pose',
  'loupe.exp.takes': 'Prises',
  'loupe.exp.defaultPose': 'pose par défaut du cadrage',
  'loupe.quality': 'Contrôle qualité',
  'loupe.garmentFidelity': 'Fidélité du vêtement',
  'loupe.identity': 'Identité',
  'loupe.scene': 'La scène',
  'loupe.publishScene': 'Proposer comme scène du Studio',
  'loupe.sceneOffered': '« {name} » est proposée à tous les clients.',
  'loupe.sceneName': 'Nom de la scène',
  'loupe.pose': 'La pose',
  'loupe.realism': 'Réalisme',
  'loupe.reshotTimes': 'Reprise {n}× pour corriger des défauts.',
  'loupe.sent': 'Ce que Gemini a reçu',
  'loupe.sentStaff': 'Équipe',
  'loupe.sentLoading': 'Chargement…',
  'loupe.sentEmpty': 'Rien d’enregistré pour cette version.',
  'loupe.sentMessage': 'Message {n}',
  'loupe.sentRefused': 'Refusé ({reason})',
  'loupe.sentAnswer': 'Réponse',
  'loupe.sentDraft': 'brouillon',
  'loupe.sentGrey': 'envoyée en gris',
  'loupe.sentVeiled': 'tête floutée',
  'loupe.sentUnveiled.none': 'tête non floutée : aucune tête repérée',
  'loupe.sentUnveiled.offline': 'tête non floutée : hors ligne',
  'loupe.sentCalls': 'Appels',
  'loupe.develop': 'Développer',
  'mark.keep': 'Garder',
  'mark.reject': 'Écarter',
  'loupe.verdict': 'Verdict',
  'loupe.kept': 'Gardée',
  'loupe.cut': 'Écartée',
  'loupe.undecided': 'Non tranchée',
  'loupe.prompt': 'Le prompt qui l’a produite',
  'loupe.negativeOnFile': 'Négatif conservé',
  'loupe.undeveloped': 'Non développée',
  'loupe.developFrame': 'Développer cette image',
  'loupe.developing': 'Développement…',
  'loupe.developFree': 'Gratuit — cela part du négatif, pas du moteur de rendu.',
  'loupe.developWait': 'Disponible une fois cette image terminée.',
  'loupe.developError': 'Impossible de développer cette image.',
  'loupe.fix': 'Corriger et reprendre',
  'loupe.fixWritten': 'Le critique a écrit cette correction. Modifiez-la, puis reprenez cette seule image.',
  'loupe.fixBlank':
    'Dites ce qu’il faut changer, puis reprenez cette seule image. Le reste de la séance n’y touche pas.',
  'loupe.fixPlaceholder': 'rendre les sequins comme de petits rectangles disposés en grille régulière',
  'loupe.fromReferences':
    'Écrite, une correction retouche la version affichée : seul ce que vous demandez change. Laissée vide, l’image est refaite depuis les références, avec toutes les corrections ci-dessous.',
  'loupe.retouch': 'Corriger cette version',
  'loupe.referenceHint':
    'C’est la photo de référence de ce lieu. Validez-la si le mannequin et le vêtement vous conviennent : les autres poses de ce lieu en seront déclinées sans y toucher.',
  'loupe.referenceApproved': 'Référence validée : les autres poses de ce lieu en sont déclinées.',
  'loupe.retouchThat': 'Corriger la version affichée',
  'loupe.fixesSoFar': 'Corrections déjà demandées',
  'loupe.fixDrop': 'Retirer la correction : {text}',
  'loupe.fixAnother': 'Une autre correction',
  'loupe.reshoot': 'Reprendre l’image {n}',
  'loupe.reshooting': 'Prise en cours…',
  'loupe.stillShooting': 'Prise en cours…',
  'loupe.reshootFailed': 'La reprise a échoué.',
  'loupe.vary': 'Variante',
  'loupe.varyHint':
    'Une variante de cette image : le même lieu, votre mannequin et votre tenue, l’appareil un pas de côté et la pose qui bouge un peu. Une seule image.',
  'loupe.varyFailed': 'La variante a échoué.',

  /* ------------------------------- sheet ------------------------------- */
  'sheet.eyebrow': 'Planche-contact',
  'insight.title': 'Ce que vous gardez',
  'insight.summary':
    '{kept} images retenues sur {judged} notées, sur {frames} prises · {downloads} téléchargées.',
  'insight.noneJudged':
    'Rien de noté pour l’instant — notez des images et ceci se remplit. {n} images prises.',
  'insight.bySetUp': 'Par cadrage',
  'insight.byGrade': 'Par étalonnage',
  'insight.byLight': 'Par lumière',
  'insight.byScene': 'Par décor',
  'insight.frames': '{n} images',
  'insight.downloads': '{n} récupérées',
  'sheet.title': 'Toutes les images',
  'sheet.display': 'Planche contact',
  'sheet.tabSheet': 'Planche',
  'sort.tab': 'Tri rapide',
  'sort.sorted': 'images triées',
  'sort.kept': '{n} gardées',
  'sort.cut': '{n} écartées',
  'sort.keptOne': '1 gardée',
  'sort.cutOne': '1 écartée',
  'sort.keep': 'Garder',
  'sort.reject': 'Écarter',
  'sort.next': 'À suivre',
  'sort.done': 'Tout est trié.',
  'sort.toSheet': 'Voir la planche',
  'sort.undo': 'Annuler la dernière',
  'sort.open': 'Agrandir cette image',
  'sheet.lede':
    'Appuyez sur une image pour l’ouvrir. Gardez celles que vous voulez d’une coche, écartez les autres d’une croix.',
  'sheet.export': 'Exporter {n} sélections',
  'sheet.exportOne': 'Exporter 1 sélection',
  'sheet.pulling': 'Extraction de {n} sélections…',
  'sheet.pullingOne': 'Extraction d’1 sélection…',
  'sheet.view.all': 'Toutes',
  'sheet.view.keep': 'Sélections',
  'sheet.view.unmarked': 'Non jugées',
  'sheet.viewLabel': 'Quelles images afficher',
  'sheet.exportSize': 'Taille d’export',
  'sheet.size1x': 'Telle quelle',
  'sheet.size2x': '2×',
  'sheet.size2xHint':
    'Rééchantillonnée au double et accentuée, pour le catalogue et l’impression. Aucun détail n’est inventé.',
  'sheet.anySetUp': 'Tous les cadrages',
  'sheet.count': '{shown} sur {total}',
  'sheet.empty': 'La planche est vide.',
  'sheet.emptyHint': 'Les images terminées de chaque séance se rassemblent ici.',
  'sheet.noKeepers': 'Rien d’entouré pour l’instant.',
  'sheet.noUnjudged': 'Plus rien à juger.',
  'sheet.filterHint': 'Changez le filtre ci-dessus pour voir le reste de la planche.',
  'sheet.keeperCount': '{n} sélections',
  'sheet.keeperCountOne': '1 sélection',
  'sheet.markKeep': 'Marquer comme sélection',
  'sheet.markKeepOff': 'Retirer la sélection',
  'sheet.markReject': 'Marquer comme rejet',
  'sheet.markRejectOff': 'Retirer le rejet',
  /* -------------------------------- compte ------------------------------ */
  'auth.signIn': 'Se connecter',
  'auth.artKicker': 'Tout au même endroit',
  'auth.artLine':
    'Vos mannequins, votre vestiaire et chaque image déjà photographiée, prêts quand vous l’êtes.',
  'auth.signInLede': 'Vos mannequins, vos séances et vos planches contact vous attendent.',
  'auth.createAccount': 'Ouvrir un studio',
  'auth.createLede': 'Un endroit pour vos mannequins, vos séances et vos planches contact.',
  'auth.forgotTitle': 'Mot de passe oublié',
  'auth.resetTitle': 'Choisir un nouveau mot de passe',
  'auth.name': 'Nom',
  'auth.email': 'Adresse e-mail',
  'auth.password': 'Mot de passe',
  'auth.newPassword': 'Nouveau mot de passe',
  'auth.passwordHint': 'Dix caractères au minimum. Long vaut mieux que compliqué.',
  'auth.working': 'Un instant…',
  'auth.sendLink': 'Envoyer le lien',
  'auth.setPassword': 'Enregistrer le mot de passe',
  'auth.forgot': 'Mot de passe oublié ?',
  'auth.needAccount': 'Ouvrir un studio',
  'auth.haveAccount': 'Retour à la connexion',
  'auth.checkMail': 'Regardez vos e-mails',
  'auth.checkMailHint': 'Si {email} est enregistrée, un lien est en route. Il reste valable une heure.',
  'auth.backToSignIn': 'Retour à la connexion',
  'auth.failed': 'Cela n’a pas fonctionné. Réessayez.',
  'auth.verifyTitle': 'Confirmer votre adresse',
  'auth.verifying': 'Un instant…',
  'auth.verified': 'Votre adresse est confirmée. Le studio est ouvert.',
  'auth.verifyFailed': 'Ce lien a expiré ou a déjà servi.',
  'auth.verifyBanner': 'Les rendus attendent que vous confirmiez {email}. Le lien est dans votre boîte.',
  'auth.verifyResend': 'Renvoyer le lien',
  'auth.verifySent': 'Un nouveau lien est en route.',
  'auth.continue': 'Entrer dans le studio',
  'auth.invitedLede':
    'Vous avez reçu une invitation pour essayer le studio. Choisissez un mot de passe pour commencer.',
  'auth.inviteInvalid': 'Cette invitation a expiré, a déjà servi ou a été annulée. Demandez-en une nouvelle.',
  'auth.inviteOnly': 'Le studio est en bêta privée : l’inscription se fait sur invitation.',
  'auth.listOnly': 'Pendant la bêta, seules les adresses autorisées peuvent ouvrir un studio.',
  'auth.inviteFor': 'L’invitation a été envoyée à cette adresse.',
  'auth.acceptBefore': 'J’accepte les',
  'auth.acceptAnd': 'et la',
  'auth.welcomeBack': 'Bon retour.',
  'auth.upTitle': 'Votre studio commence ici.',
  'auth.doors': 'Se connecter ou ouvrir un studio',
  'auth.word1': 'Recréez.',
  'auth.word2': 'Composez.',
  'auth.word3': 'Décrivez.',
  'legal.terms': 'conditions d’utilisation',
  'legal.privacy': 'politique de confidentialité',
  'error.invite_required': 'Le studio est en bêta privée : l’inscription se fait sur invitation.',
  'error.invite_invalid': 'Cette invitation n’est plus valable, ou elle a été envoyée à une autre adresse.',

  'settings.eyebrow': 'Compte',
  'settings.noAccounts': 'Ce studio fonctionne sans comptes : il n’y a rien à régler ici.',
  'settings.title': 'Réglages',
  'settings.signOut': 'Se déconnecter',
  'settings.tab.profile': 'Profil',
  'settings.tab.usage': 'Utilisation',
  'settings.tab.security': 'Sécurité',
  'settings.tab.appearance': 'Apparence',
  'settings.theme': 'Thème',
  'settings.nameHint': 'Ce que voient les autres personnes du compte.',
  'settings.studioHint': 'Commun à tout le compte.',
  'settings.themeHint': 'Système suit le réglage de l’appareil.',
  'settings.languageHint': 'Gardée avec votre compte.',
  'settings.signOutHint': 'Sur cet appareil.',
  'usage.thisMonth': 'Ce mois-ci',
  'usage.frames': 'Images réalisées',
  'usage.noLimit':
    'Aucune formule, aucun plafond : créez autant que vous voulez. Chaque image est facturée par le fournisseur sur votre propre clé d’API.',
  'settings.you': 'Vous',
  'settings.emailFixed': 'Votre adresse sert à vous connecter. Écrivez-nous si elle doit changer.',
  'settings.studio': 'Studio',
  'settings.studioName': 'Nom du studio',
  'settings.members': 'Personnes sur le compte',
  'settings.saved': 'Enregistré.',
  'settings.saveFailed': 'Impossible d’enregistrer.',
  'settings.password': 'Mot de passe',
  'settings.currentPassword': 'Mot de passe actuel',
  'settings.changePassword': 'Changer le mot de passe',
  'settings.passwordChanged': 'Mot de passe changé. Les autres appareils sont déconnectés.',
  'settings.passwordEvicts': 'Le changer déconnecte tous les autres appareils.',
  'settings.devices': 'Sessions ouvertes',
  'settings.revoke': 'Fermer',
  'settings.thisDevice': 'Cet appareil',
  'settings.deviceOn': '{browser} sur {os}',
  'settings.deviceUnknown': 'Appareil inconnu',
  'settings.lastSeen': 'Utilisé le {when}',
  'settings.devicesMore': 'Voir les {n} autres',
  'settings.devicesFewer': 'Voir moins',
  'settings.activity': 'Activité récente du compte',
  'settings.dangerZone': 'Fermer le studio',
  'settings.deleteWarning':
    'Cela supprime {name} et tout ce qu’il contient — mannequins, séances, images et fichiers. C’est sans retour.',
  'settings.typeName': 'Tapez « {name} » pour confirmer',
  'settings.deleteAccount': 'Supprimer ce compte',

  'calib.title': 'Le juge, mesuré',
  'calib.notEnough':
    'Notez des images sur la planche-contact et ceci se remplit — {n} sur {min} pour l’instant.',
  'calib.agreement': 'Le critique et vous êtes d’accord sur {pct} des {n} images notées.',
  'calib.falseRejects': 'Refaites, vous les gardiez',
  'calib.falseAccepts': 'Livrées, vous les avez barrées',
  'calib.keptMean': 'Note moyenne, gardées',
  'calib.rejectedMean': 'Note moyenne, barrées',
  'calib.suggested': 'Un seuil de {n} aurait le mieux collé à vos notes — {pct} d’accord.',

  'error.account_suspended': 'Ce compte est suspendu. Écrivez-nous et nous arrangerons cela.',
  'error.email_unverified':
    'Confirmez votre adresse e-mail avant de lancer un rendu. Le lien est dans votre boîte.',
  'error.missing_reference':
    'Un visage ou une pièce de cette séance n’est plus au catalogue. Recréez-le ou ajoutez-le à nouveau.',
  'error.veil_failed':
    'La tête du mannequin de la scène n’a pas pu être repérée pour être floutée : rien n’a été envoyé ni facturé. Relancez dans un instant.',
  'error.too_many_running': 'Ce compte a déjà {max} séances en cours. Attendez qu’une se termine.',
  'error.spend_ceiling':
    'Les rendus sont suspendus pour la journée : le studio a atteint sa limite de dépenses quotidienne.',
  'error.verify_invalid': 'Ce lien a expiré ou a déjà servi.',
  'error.mail_unavailable': 'Ce serveur ne peut pas envoyer d’e-mails, il n’y a donc pas de lien à envoyer.',

  /* --------------------------------- régie ------------------------------ */
  'admin.eyebrow': 'Régie',
  'admin.title': 'La maison',
  'admin.lede': 'Qui est là, et ce que le studio a photographié.',
  'admin.tab.overview': 'Vue d’ensemble',
  'admin.tab.catalogue': 'Catalogue',
  'cat.shelf.scenes': 'Scènes',
  'cat.addScene': 'Ajouter une scène',
  'cat.addSceneHint': 'Une photo finie : lieu, pose, tenue, votre mannequin',
  'cat.scenesHint':
    'Chaque scène est une photo finie. Les clients y mettent leur mannequin et leurs vêtements à la place des vôtres ; tout le reste est gardé. Les vignettes montrent la photo telle que Gemini la reçoit : la tête du mannequin floutée.',
  'cat.emptyScenes':
    'Aucune scène pour l’instant. Ajoutez une photo, ou proposez une image depuis une de vos séances.',
  'cat.removeScene': 'Retirer « {name} » ?',
  'cat.sceneName': 'Nom de la scène',
  'cat.lede':
    'Ce qui est proposé à tous les clients dans « Nouvelle séance » : modèles, lieux, poses et scènes. Les clients peuvent les utiliser ; vous seul pouvez les modifier.',
  'cat.shelf.models': 'Modèles',
  'cat.shelf.places': 'Lieux',
  'cat.shelf.poses': 'Poses',
  'cat.addModel': 'Nouveau modèle',
  'cat.edit': 'Modifier',
  'cat.saved': 'Enregistré dans le catalogue.',
  'cat.failed': 'Impossible de faire cela.',
  'cat.removeBody': 'Il disparaît des choix de tous les clients. Les séances déjà faites le gardent.',
  'cat.emptyModels': 'Pas encore de modèle dans le catalogue : créez le premier.',
  'cat.emptyPlaces':
    'Pas encore de lieu à vous dans le catalogue. Les décors intégrés, ci-dessous, sont toujours proposés.',
  'cat.placesHint':
    'Cliquez sur un lieu pour modifier son nom, sa description ou sa photo, ou pour le supprimer. Les décors intégrés sont proposés en plus.',
  'cat.builtinPlaces': 'Décors intégrés',
  'cat.builtin': 'Intégré',
  'cat.builtinHint':
    'Proposés dans chaque séance, en plus des lieux du catalogue. Ils font partie du studio et ne se modifient pas ici.',
  'cat.emptyPoses':
    'Pas encore de pose : ajoutez des photos de personnes dans les poses à proposer, une pose par photo.',
  'cat.posesHint':
    'Cliquez sur une pose pour la renommer. Rien n’est lu ni généré : la photo est reproduite telle quelle.',
  'admin.tab.accounts': 'Studios',
  'admin.tab.invites': 'Invitations',
  'admin.invite.new': 'Nouvelle invitation',
  'admin.invite.hint':
    'Avec une adresse, le lien ne sert qu’à elle et part par e-mail si l’envoi d’e-mails est configuré. Sans adresse, il sert à la première personne qui l’ouvre. Valable 14 jours, une seule fois.',
  'admin.invite.email': 'Adresse e-mail (facultatif)',
  'admin.invite.emailPlaceholder': 'prenom@exemple.fr',
  'admin.invite.note': 'Note (facultatif)',
  'admin.invite.notePlaceholder': 'Pour qui, pourquoi',
  'admin.invite.create': 'Créer l’invitation',
  'admin.invite.emailed': 'Invitation envoyée à {email}. Le lien est aussi ci-dessous.',
  'admin.invite.copyNow': 'Copiez ce lien maintenant : il ne sera plus affiché.',
  'admin.invite.link': 'Lien d’invitation',
  'admin.invite.copy': 'Copier',
  'admin.invite.copied': 'Lien copié.',
  'admin.invite.count': 'Invitations : {n}',
  'admin.invite.open': 'Lien sans adresse',
  'admin.invite.usedBy': 'Utilisée par {email} le {date}',
  'admin.invite.expires': 'Expire le {date}',
  'admin.invite.revoke': 'Annuler',
  'admin.invite.status.pending': 'En attente',
  'admin.invite.status.used': 'Utilisée',
  'admin.invite.status.expired': 'Expirée',
  'admin.invite.status.revoked': 'Annulée',
  'admin.failed': 'Cela n’a pas fonctionné.',
  'admin.done': 'C’est fait.',

  'admin.stat.accounts': 'Studios',
  'admin.stat.new30': '{n} ouverts en 30 jours',
  'admin.stat.people': 'Personnes',
  'admin.stat.active30': '{n} connectées ce mois-ci',
  'admin.stat.framesPeriod': 'Images cette période',
  'admin.stat.spend': 'Dépense cette période',
  'admin.stat.spendFoot': 'Appels aux prestataires, sur {n} images',
  'admin.stat.perFrame': 'Coût par image',
  'admin.stat.perFrameFoot': 'Rendus, contrôles et prompts compris',
  'admin.stat.spendTop': 'Premier poste',
  'admin.detail.spend': 'Dépense cette période',
  'roll.cost': 'Coût',
  'roll.costHint': '{render} de rendu, {critique} de contrôle, {setUp} de préparation',
  'admin.stat.rolls': 'Séances',
  'admin.stat.models': '{n} mannequins',
  'admin.stat.frames': 'Images, depuis le début',
  'admin.stat.failed': '{n} échecs',
  'admin.stat.storage': 'Stockage',
  'admin.stat.storageFoot': 'Imports et rendus',

  'admin.chart.frames': 'Images par jour, quinzaine écoulée',
  'admin.chart.total': '{n} en 14 jours',
  'admin.chart.empty': 'Aucune image terminée durant la quinzaine écoulée.',
  'admin.chart.aria': '{n} images terminées sur les {days} derniers jours.',

  'admin.health': 'Bon à savoir',
  'admin.health.suspended': 'Suspendus',
  'admin.health.staff': 'Régie',
  'admin.health.staffHint':
    'La régie vient de ADMIN_EMAILS, côté serveur. Rien dans cette application ne peut l’accorder, et c’est voulu.',

  'admin.searchPlaceholder': 'Nom, identifiant de compte ou e-mail',
  'admin.accountsFound': '{n} studios',
  'admin.suspended': 'suspendu',
  'admin.framesShort': '{n} images',
  'admin.detail.frames': 'Images réalisées',
  'admin.detail.storage': 'Stockage',

  'admin.danger': 'Mesures fortes',
  'admin.suspend': 'Suspendre ce studio',
  'admin.restore': 'Lever la suspension',
  'admin.deleteAccount': 'Supprimer ce studio',
};

/**
 * Preset names and descriptions.
 *
 * Kept apart from the interface copy because these keys are derived from
 * preset ids rather than written by hand: a mistyped one simply shows the
 * English label, which you notice the moment you look at the picker. The UI
 * dictionary above stays strictly typed so a mistyped key there is a build
 * error instead.
 *
 * The presets themselves stay English — the prompts are built from the same
 * objects, and the renderers work best in English.
 */
export const frPresets: Record<string, string> = {
  /* ---------------------------- camera set-ups -------------------------- */
  'pov.full-front.name': 'Plein pied — face',
  'pov.full-front.desc': 'La vue e-commerce. De face, silhouette entière, sans recadrage.',
  'pov.full-three-quarter.name': 'Plein pied — 3/4',
  'pov.full-three-quarter.desc': 'Corps à ~45°. Montre le tombé et les coutures latérales.',
  'pov.full-profile.name': 'Plein pied — profil',
  'pov.full-profile.desc': 'Profil pur, pour la silhouette et la longueur.',
  'pov.full-back.name': 'Plein pied — dos',
  'pov.full-back.desc': 'Construction arrière : empiècements, fentes, poches dos, tombé d’ourlet.',
  'pov.three-quarter-crop.name': 'Plan américain',
  'pov.three-quarter-crop.desc': 'Mi-cuisse à la tête. Le format de travail du catalogue.',
  'pov.bust-portrait.name': 'Portrait poitrine',
  'pov.bust-portrait.desc': 'Encolure, col, boucles d’oreilles, maquillage.',
  'pov.seated.name': 'Assise',
  'pov.seated.desc': 'Assise sur un tabouret — montre les plis du tissu en position assise.',
  'pov.detail-fabric.name': 'Détail matière',
  'pov.detail-fabric.desc': 'Macro sur le tissage, les sequins, la broderie, la texture.',
  'pov.detail-neckline.name': 'Encolure & épaule',
  'pov.detail-neckline.desc': 'Col, finition d’emmanchure, couture d’épaule, superpositions.',
  'pov.detail-hem.name': 'Ourlet & chaussures',
  'pov.detail-hem.desc': 'Cassure du pantalon, finition d’ourlet, chaussures.',
  'pov.walking.name': 'En marche',
  'pov.walking.desc': 'Le mouvement — comment le vêtement se comporte en marchant.',
  'pov.over-shoulder.name': 'Par-dessus l’épaule',
  'pov.over-shoulder.desc': 'De dos, regard en arrière. Un classique éditorial.',
  'pov.candid-motion.name': 'Volte spontanée',
  'pov.candid-motion.desc': 'Une énergie libre et non posée, sans quitter le studio.',
  'pov.low-angle.name': 'Contre-plongée',
  'pov.low-angle.desc': 'Objectif sous la ligne des yeux. Allonge la jambe, impose.',
  'pov.high-angle.name': 'Plongée',
  'pov.high-angle.desc': 'Légèrement au-dessus des yeux, regard vers l’objectif.',

  /* -------------------------------- poses ------------------------------- */
  'pose.weight-shift.name': 'Appui déhanché',
  'pose.weight-shift.desc': 'Le réglage par défaut du catalogue. Contrapposto détendu.',
  'pose.hands-pockets.name': 'Mains dans les poches',
  'pose.hands-pockets.desc': 'Les deux mains enfouies. Lecture facile, non posée.',
  'pose.arms-relaxed.name': 'Bras le long du corps',
  'pose.arms-relaxed.desc': 'Rien ne masque le vêtement.',
  'pose.hand-hip.name': 'Main sur la hanche',
  'pose.hand-hip.desc': 'Ouvre la silhouette, dessine la taille.',
  'pose.arms-crossed.name': 'Bras croisés',
  'pose.arms-crossed.desc': 'Fermée, refermée sur elle-même.',
  'pose.holding-bag.name': 'Sac à la main',
  'pose.holding-bag.desc': 'Pour les séances où le sac est le produit.',
  'pose.adjusting-sleeve.name': 'Ajuste une manche',
  'pose.adjusting-sleeve.desc': 'Les mains attirées vers un détail. Lecture non posée.',
  'pose.mid-stride.name': 'En pleine foulée',
  'pose.mid-stride.desc': 'Marche vers l’objectif. Montre le mouvement du tissu.',
  'pose.carrying-weight.name': 'En marche, sac à la main',
  'pose.carrying-weight.desc': 'En pleine foulée, un sac lourd tire une épaule vers le bas.',
  'pose.hood-up.name': 'Capuche relevée',
  'pose.hood-up.desc': 'Capuche remontée sur la tête, encadrant le visage.',
  'pose.wind-in-hair.name': 'Cheveux dans le vent',
  'pose.wind-in-hair.desc': 'Cheveux libres balayés sur le visage. Demande un lieu exposé.',
  'pose.walk-away-glance.name': 'S’éloigne, regard en arrière',
  'pose.walk-away-glance.desc': 'Montre le dos d’un vêtement en mouvement.',
  'pose.turning-arms-out.name': 'Volte, bras écartés',
  'pose.turning-arms-out.desc': 'Ample, cinétique, énergie de campagne.',
  'pose.hair-sweep.name': 'Main dans les cheveux',
  'pose.hair-sweep.desc': 'Du vent dans les cheveux. Adoucit une image statique.',
  'pose.coat-open.name': 'Manteau tenu ouvert',
  'pose.coat-open.desc': 'Montre une doublure et les couches en dessous.',
  'pose.reclining-elbow.name': 'Allongée sur un coude',
  'pose.reclining-elbow.desc': 'Classique éditorial. Demande de l’herbe, du sable ou un sol.',
  'pose.seated-leaning-back.name': 'Assise, appuyée sur une main',
  'pose.seated-leaning-back.desc': 'Assise dans l’herbe ou le sable, en appui arrière sur un bras.',
  'pose.lying-back.name': 'Allongée sur le dos',
  'pose.lying-back.desc': 'Vue du dessus. Le tissu s’étale à plat.',
  'pose.seated-ground.name': 'Assise au sol',
  'pose.seated-ground.desc': 'Genoux relevés, avant-bras posés. Décontracté.',
  'pose.crouching.name': 'Accroupie',
  'pose.crouching.desc': 'Compact. Idéal pour les manteaux et les bottes.',
  'pose.seated-stool.name': 'Assise sur un tabouret',
  'pose.seated-stool.desc': 'L’assise de studio. Montre les plis du tissu assise.',
  'pose.seated-steps.name': 'Assise sur des marches',
  'pose.seated-steps.desc': 'Une jambe tendue, l’autre repliée.',
  'pose.leaning-fence.name': 'Appuyée sur une barrière',
  'pose.leaning-fence.desc': 'Avant-bras sur une clôture. Éditorial rural détendu.',
  'pose.leaning-wall.name': 'Épaule au mur',
  'pose.leaning-wall.desc': 'Une épaule plantée, chevilles croisées.',
  'pose.hand-on-rock.name': 'Main sur un rocher',
  'pose.hand-on-rock.desc': 'Ancre le modèle dans un décor extérieur.',
  'pose.looking-away.name': 'Regard hors champ',
  'pose.looking-away.desc': 'Distance éditoriale. Rien n’est vendu à l’objectif.',
  'pose.over-shoulder-glance.name': 'Par-dessus l’épaule',
  'pose.over-shoulder-glance.desc': 'Dos à l’objectif, le visage y revient.',
  'pose.hand-to-jaw.name': 'Main près du visage',
  'pose.hand-to-jaw.desc': 'Fait entrer une main dans un cadrage portrait.',

  'pose.jump-frozen.name': 'Saut figé',
  'pose.jump-frozen.desc': 'Les deux pieds décollés, au sommet du saut.',
  'pose.spin-turn.name': 'Tour sur soi',
  'pose.spin-turn.desc': 'Un tour complet, la jupe ou le manteau qui s’évase.',
  'pose.running.name': 'Course',
  'pose.running.desc': 'Une vraie course à travers le cadre, pas un jogging sur place.',
  'pose.stride-stairs.name': 'Dans l’escalier',
  'pose.stride-stairs.desc': 'Les marches deux par deux, un regard en arrière.',
  'pose.dance-step.name': 'Pas de danse',
  'pose.dance-step.desc': 'Un pas libre, sans chorégraphie, sur une musique que personne n’entend.',
  'pose.kick-back.name': 'Talon levé',
  'pose.kick-back.desc': 'Un talon relevé derrière, espiègle.',
  'pose.cafe-table.name': 'En terrasse',
  'pose.cafe-table.desc': 'Assise à une petite table ronde, un café à la main.',
  'pose.coffee-walk.name': 'Café à emporter',
  'pose.coffee-walk.desc': 'En marche, gobelet à la main.',
  'pose.crossing-street.name': 'Traversée',
  'pose.crossing-street.desc': 'Au milieu du passage piéton, un regard de côté.',
  'pose.phone-call.name': 'Au téléphone',
  'pose.phone-call.desc': 'Le téléphone à l’oreille, en pleine conversation.',
  'pose.leaning-doorway.name': 'Dans l’encadrement',
  'pose.leaning-doorway.desc': 'L’épaule contre le chambranle, bras croisés.',
  'pose.stoop-seated.name': 'Sur le perron',
  'pose.stoop-seated.desc': 'Assise sur une marche, les coudes sur les genoux.',
  'pose.bar-counter.name': 'Au comptoir',
  'pose.bar-counter.desc': 'Un coude sur le zinc, un verre à la main.',
  'pose.bench-reading.name': 'Lecture sur un banc',
  'pose.bench-reading.desc': 'Plongée dans un livre de poche, jambes croisées.',
  'pose.bike-lean.name': 'À vélo',
  'pose.bike-lean.desc': 'À cheval sur un vélo de ville, un pied au sol.',
  'pose.hailing-taxi.name': 'Hèle un taxi',
  'pose.hailing-taxi.desc': 'Un bras levé vers la rue, le sac à l’épaule.',
  'pose.window-shopping.name': 'Lèche-vitrine',
  'pose.window-shopping.desc': 'Arrêtée devant une vitrine, vue de trois quarts.',
  'pose.laughing-off-frame.name': 'Rire hors champ',
  'pose.laughing-off-frame.desc': 'En plein rire, tournée vers quelqu’un hors cadre.',

  'scene.paris-street.name': 'Rue parisienne',
  'scene.paris-cafe-terrace.name': 'Terrasse de café',
  'scene.paris-cafe-interior.name': 'Intérieur de café',
  'scene.paris-bar.name': 'Bar parisien, le soir',
  'scene.paris-doorway.name': 'Porte cochère haussmannienne',
  'scene.paris-quay.name': 'Quai de Seine',
  'scene.paris-metro.name': 'Bouche de métro',
  'scene.paris-passage.name': 'Passage couvert',
  'scene.paris-montmartre.name': 'Escaliers de Montmartre',
  'scene.paris-bridge.name': 'Pont sur la Seine',

  'accessory.sunglasses': 'Lunettes de soleil',
  'accessory.glasses': 'Lunettes de vue',
  'accessory.necklace': 'Collier',
  'accessory.earrings': 'Boucles d’oreilles',
  'accessory.ring': 'Bague',
  'accessory.bracelet': 'Bracelet',
  'accessory.watch': 'Montre',
  'accessory.belt': 'Ceinture',
  'accessory.scarf': 'Foulard',
  'accessory.cap': 'Casquette',
  'accessory.beanie': 'Bonnet',
  'accessory.hat': 'Chapeau à larges bords',
  'accessory.handbag': 'Sac à main',
  'accessory.tote': 'Cabas',
  'accessory.crossbody': 'Sac bandoulière',

  'pose.stride-pocket.name': 'Main dans la poche, en marche',
  'pose.stride-pocket.desc': 'Passe devant l’objectif, une main dans la poche du pantalon.',
  'pose.kerb-bag.name': 'Au bord du trottoir, sac en main',
  'pose.kerb-bag.desc': 'Attend pour traverser, un petit sac tenu bas par les anses.',
  'pose.cafe-menu.name': 'La carte en main',
  'pose.cafe-menu.desc': 'Assise au café, la carte ouverte, un regard pour répondre à quelqu’un.',
  'scene.paris-kerb.name': 'Au carrefour',
  'accessory.slim-sunglasses': 'Lunettes de soleil fines',
  'accessory.bangles': 'Joncs dorés',
  'light.street-shade.name': 'Ombre de rue',
  'light.street-shade.desc':
    'Ombre ouverte sur une rue de pierre. Chaude, douce, sans soleil. Le réglage street-style par défaut.',
  'recipe.paris-street-style.name': 'Street style parisien',
  'recipe.paris-street-style.desc': 'En marche dans la rue et au carrefour, trois poses, film chaud.',
  'recipe.paris-cafe.name': 'Café parisien',
  'recipe.paris-cafe.desc':
    'Assise à l’intérieur et en terrasse, en pleine conversation, lumière de fenêtre.',
  'recipe.paris-crossing.name': 'Au carrefour',
  'recipe.paris-crossing.desc': 'Au bord du trottoir devant la grille dorée, en pied, lunettes sur le nez.',

  'pose.cross-step.name': 'Pas croisé',
  'pose.cross-step.desc': 'Un pied croise devant l’autre, les hanches de biais.',
  'pose.hair-tuck.name': 'Mèche derrière l’oreille',
  'pose.hair-tuck.desc': 'Replace une mèche derrière l’oreille, regard levé.',
  'pose.sunglasses-lower.name': 'Par-dessus les lunettes',
  'pose.sunglasses-lower.desc': 'Baisse ses lunettes de soleil pour regarder par-dessus.',
  'pose.look-back-smile.name': 'Regard en arrière, sourire',
  'pose.look-back-smile.desc': 'S’éloigne et se retourne avec un sourire.',
  'pose.walk-look-down.name': 'En marche, regard baissé',
  'pose.walk-look-down.desc': 'Avance vers l’objectif en regardant le trottoir.',
  'pose.collar-hold.name': 'Mains au col',
  'pose.collar-hold.desc': 'Les deux mains aux revers de la veste.',
  'pose.wall-knee.name': 'Contre le mur, genou plié',
  'pose.wall-knee.desc': 'Dos au mur, un pied à plat contre lui.',
  'pose.laugh-hand.name': 'Rire derrière la main',
  'pose.laugh-hand.desc': 'Un vrai rire, à moitié caché derrière les doigts.',
  'pose.steps-crossed.name': 'Sur les marches, jambes croisées',
  'pose.steps-crossed.desc': 'Assise sur des marches, jambes croisées, coude sur le genou.',
  'pose.hat-brim.name': 'Main sur le bord du chapeau',
  'pose.hat-brim.desc': 'Un chapeau à large bord, une main sur le bord. Les mains ont un rôle.',
  'pose.hand-on-face.name': 'Main sur le visage',
  'pose.hand-on-face.desc': 'Bout des doigts le long de la pommette, l’autre bras le long du corps.',
  'pose.hands-on-hips.name': 'Mains sur les hanches',
  'pose.hands-on-hips.desc': 'Les deux mains sur les hanches, coudes ouverts. De l’attitude.',
  'pose.hands-in-hair.name': 'Mains dans les cheveux',
  'pose.hands-in-hair.desc': 'Les deux mains passées dans les cheveux. Naturel, pas posé.',
  'pose.jacket-wrap.name': 'Enveloppée dans la veste',
  'pose.jacket-wrap.desc': 'Elle serre sa veste ou son gilet autour d’elle. Joueur.',
  'pose.sit-forward.name': 'Assise, penchée en avant',
  'pose.sit-forward.desc': 'Sur un tabouret bas, avant-bras sur les genoux, visage vers l’objectif.',
  'pose.wall-shoulder.name': 'Épaule contre le mur',
  'pose.wall-shoulder.desc': 'Une épaule contre le mur, chevilles croisées, regard par-dessus l’épaule.',
  'pose.knee-pop.name': 'Genou plié en avant',
  'pose.knee-pop.desc': 'Poids en arrière, genou avant plié, pointe au sol. Donne du relief.',
  'pose.leg-cross-stand.name': 'Debout, jambes croisées',
  'pose.leg-cross-stand.desc': 'Une jambe croisée devant l’autre, hanches de biais.',
  'pose.product-present.name': 'Montrer le sac',
  'pose.product-present.desc': 'Le sac tenu près de l’épaule, tourné vers l’objectif. Commercial.',
  'pose.stare-down.name': 'Regard frontal intense',
  'pose.stare-down.desc': 'Face à l’objectif, menton baissé, regard intense et immobile.',
  'pose.eyes-closed.name': 'Yeux fermés',
  'pose.eyes-closed.desc': 'Yeux doucement fermés, visage levé. Calme, intérieur.',
  'pose.hand-on-head.name': 'Main sur la tête, détournée',
  'pose.hand-on-head.desc': 'Une main posée sur la tête, yeux fermés, de trois quarts dos. Mystérieux.',
  'pose.angular-editorial.name': 'Angles haute couture',
  'pose.angular-editorial.desc': 'Coude levé, genou rentré, buste en torsion. Audacieux.',
  'pose.shadow-turn.name': 'À moitié dans l’ombre',
  'pose.shadow-turn.desc': 'Le visage tourné vers la lumière, une moitié dans l’ombre.',
  'recipe.paris-influencer.name': 'Instagrameuse',
  'recipe.paris-influencer.desc':
    'Poses d’influenceuse dans une rue parisienne, détendue et souriante, tenue du jour.',

  /* -------------------------------- scenes ------------------------------ */
  'scene.seamless-brown.name': 'Fond brun',
  'scene.seamless-white.name': 'Fond blanc',
  'scene.seamless-grey.name': 'Fond gris',
  'scene.seamless-bone.name': 'Ivoire',
  'scene.seamless-black.name': 'Fond noir',
  'scene.seamless-sand.name': 'Sable',
  'scene.seamless-sage.name': 'Sauge',
  'scene.concrete.name': 'Mur de béton',
  'scene.paper-cyc.name': 'Cyclorama',
  'scene.moorland-field.name': 'Champ ouvert',
  'scene.black-sand-beach.name': 'Sable noir & montagne',
  'scene.dune-grass.name': 'Dunes & ressac',
  'scene.boulder-field.name': 'Rochers & pâturage',
  'scene.coastal-cliff.name': 'Cap côtier',
  'scene.highland-loch.name': 'Lande & loch',
  'scene.desert-scrub.name': 'Garrigue désertique',
  'scene.rural-fence.name': 'Champ & clôture',
  'scene.wildflower-meadow.name': 'Prairie fleurie',
  'scene.forest-edge.name': 'Lisière de forêt',
  'scene.stone-street.name': 'Vieille rue',
  'scene.concrete-plaza.name': 'Esplanade de béton',

  /* ------------------------------- lighting ----------------------------- */
  'light.match-setting.name': 'La lumière du lieu',
  'light.match-setting.desc': 'Éclairée comme le lieu l’est : même direction, même couleur, même douceur.',
  'light.high-key-soft.name': 'Commercial high-key',
  'light.high-key-soft.desc': 'Douce, homogène, presque sans ombre. Le rendu e-commerce par défaut.',
  'light.soft-box-natural.name': 'Boîte à lumière, chute naturelle',
  'light.soft-box-natural.desc': 'Une grande source, ombre douce du côté opposé.',
  'light.editorial-contrast.name': 'Contraste éditorial',
  'light.editorial-contrast.desc': 'Lumière principale directionnelle, ombres profondes, modelé.',
  'light.hard-flash.name': 'Flash direct',
  'light.hard-flash.desc': 'Flash sur l’appareil, ombre nette, énergie campagne années 90.',
  'light.overcast-soft.name': 'Ciel couvert',
  'light.overcast-soft.desc': 'Nuages épais. Plate, sans ombre, froide. Le réglage extérieur par défaut.',
  'light.broken-cloud.name': 'Trouée dans les nuages',
  'light.broken-cloud.desc': 'Soleil rasant sous un ciel sombre. Spectaculaire, et la plus dure à imiter.',
  'light.golden-hour.name': 'Heure dorée',
  'light.golden-hour.desc': 'Soleil bas et chaud, ombres longues, léger halo.',
  'light.coastal-grey.name': 'Gris côtier',
  'light.coastal-grey.desc': 'Brume marine lumineuse. Froide et désaturée.',
  'light.desert-sun.name': 'Soleil au zénith',
  'light.desert-sun.desc': 'Lumière dure de midi, ombres franches.',
  'light.blue-hour.name': 'Heure bleue',
  'light.blue-hour.desc': 'Juste après le coucher. Froide, faible, sans soleil direct.',
  'light.window-daylight.name': 'Lumière de fenêtre',
  'light.window-daylight.desc': 'Jour diffus et froid, rasant depuis un côté.',
  'light.rim-backlight.name': 'Contre-jour / liseré',
  'light.rim-backlight.desc': 'Halo de séparation le long du contour.',

  /* -------------------------------- grades ------------------------------ */
  'grade.clean-digital.name': 'Numérique neutre',
  'grade.clean-digital.desc': 'Couleurs fidèles. Pour l’e-commerce.',
  'grade.muted-editorial.name': 'Éditorial sourd',
  'grade.muted-editorial.desc': 'Désaturé, contraste doux, grain fin. Allure campagne.',
  'grade.warm-film.name': 'Argentique chaud',
  'grade.warm-film.desc': 'Chaleur analogique, halo léger, grain visible.',
  'grade.cool-nordic.name': 'Nordique froid',
  'grade.cool-nordic.desc': 'Froid, gris-vert, très peu saturé.',
  'grade.rich-contrast.name': 'Contraste soutenu',
  'grade.rich-contrast.desc': 'Noirs profonds, couleurs saturées, punch.',
  'grade.black-white.name': 'Noir & blanc',
  'grade.black-white.desc': 'Monochrome, gamme tonale complète.',

  /* ------------------------------- stocks ------------------------------- */
  'stock.digital.name': 'Numérique',
  'stock.digital.desc': 'Propre. Aucun développement n’est appliqué.',
  'stock.fine-400.name': 'Grain fin 400',
  'stock.fine-400.desc': 'Négatif couleur moderne. Un grain qu’on sent plus qu’on ne le voit.',
  'stock.pushed-800.name': 'Poussée 800',
  'stock.pushed-800.desc': 'Un grain lisible. Ombres plus froides, noirs plus doux.',
  'stock.pushed-1600.name': 'Poussée 1600',
  'stock.pushed-1600.desc': 'Grain marqué, noirs plats. Éditorial de fin de nuit.',
  'stock.faded-70s.name': 'Délavé 70s',
  'stock.faded-70s.desc': 'Dominante chaude, noirs laiteux, halo. La vraie vintage.',
  'stock.mono-400.name': 'Monochrome 400',
  'stock.mono-400.desc': 'Noir et blanc, gamme complète, vrai grain d’argent.',
  'stock.slide-100.name': 'Diapo 100',
  'stock.slide-100.desc': 'Grain très fin, noirs denses, couleurs saturées.',

  /* ------------------------------ apertures ----------------------------- */
  'aperture.auto': 'Ce que le lieu demande.',
  'aperture.f1.4': 'Elle seule est nette ; le lieu se fond dans le flou.',
  'aperture.f2': 'Elle nette, le lieu nettement flou.',
  'aperture.f2.8': 'Le lieu doucement flou, encore lisible.',
  'aperture.f4': 'Le lieu à peine flou.',
  'aperture.f5.6': 'Presque tout le lieu net.',
  'aperture.f8': 'Grande profondeur : elle et le lieu nets.',
  'aperture.f11': 'Tout est net, du premier plan au fond.',
};
